"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.audio = void 0;
const logger_1 = require("../util/logger");
const toolbox_1 = require("@streem/toolbox");
const AUDIO_EVENTS_TO_CAPTURE = [
    'canplay',
    'canplaythrough',
    'emptied',
    'playing',
    'pause',
    'ended',
    'volumechange',
    'suspend',
    'waiting',
    'stalled',
];
class AppAudio {
    constructor() {
        this.playReservationAudio = () => __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.reservationAudio, 'Reservation audio is not initialized');
            if (this.reservationAudio) {
                yield this.setPreferredAudioOutput(this.reservationAudio);
                logger_1.logger.info(`Playing reservation audio`);
                yield this.reservationAudio.play();
            }
        });
        this.pauseReservationAudio = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            logger_1.logger.info(`Pausing reservation audio`);
            (_a = this.reservationAudio) === null || _a === void 0 ? void 0 : _a.pause();
        });
    }
    initialize(handleAudioOutputDeviceChanged, handleRingtonePlaying) {
        this.initRingtone();
        this.handleAudioOutputDeviceChanged = handleAudioOutputDeviceChanged;
        this.handleRingtonePlaying = handleRingtonePlaying;
    }
    playRingtoneAudio() {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.ringtoneAudio, 'Ringtone audio is not initialized');
            if (this.ringtoneAudio) {
                yield this.setPreferredAudioOutput(this.ringtoneAudio);
                logger_1.logger.info(`Playing ringtone`);
                this.ringtonePromise = this.ringtoneAudio.play();
                this.ringtoneAudioPaused = this.ringtoneAudio.paused;
            }
        });
    }
    pauseRingtoneAudio() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.ringtonePromise !== undefined) {
                try {
                    yield this.ringtonePromise;
                    (_a = this.ringtoneAudio) === null || _a === void 0 ? void 0 : _a.pause();
                    logger_1.logger.info('Ringtone successfully paused: ', (_b = this.ringtoneAudio) === null || _b === void 0 ? void 0 : _b.paused);
                    this.ringtoneAudioPaused = (_c = this.ringtoneAudio) === null || _c === void 0 ? void 0 : _c.paused;
                    (_d = this.ringtoneAudio) === null || _d === void 0 ? void 0 : _d.load();
                    this.ringtonePromise = undefined;
                }
                catch (error) {
                    logger_1.logger.error(`Failed to pause ringtone: ${error}`);
                }
            }
            else {
                logger_1.logger.warn('Attempted to pause a ringtone before calling `play()`');
            }
        });
    }
    removeReservationAudioListener() {
        var _a;
        (_a = this.reservationAudio) === null || _a === void 0 ? void 0 : _a.removeEventListener('ended', this.endedListenerHandler);
    }
    setPreferredAudioOutput(audioElement) {
        return __awaiter(this, void 0, void 0, function* () {
            // @ts-expect-error: ts2339 setSinkId is not in the HTMLAudioElement type
            if (typeof audioElement.setSinkId !== 'function') {
                logger_1.logger.info(`Browser does not support setSinkId.`);
                return;
            }
            const preferredOutputId = (0, toolbox_1.getPreferredAudioOutputId)();
            let sinkId = preferredOutputId || 'default';
            const devices = yield (0, toolbox_1.getMediaDevices)('audiooutput');
            if (!devices.some((device) => device.deviceId === preferredOutputId)) {
                logger_1.logger.warn(`Could not find preferred preferredOutputId=${preferredOutputId} Resetting preferred device to default`);
                logger_1.logger.info(`Available devices=${JSON.stringify(devices)}`);
                (0, toolbox_1.setPreferredAudioOutputId)('default');
                sinkId = 'default';
            }
            // @ts-expect-error: ts2339 setSinkId is not in the HTMLAudioElement type
            yield audioElement.setSinkId(sinkId);
            document.body.appendChild(audioElement);
            logger_1.logger.info(`Audio output set to preferred output ${(0, toolbox_1.getPreferredAudioOutputId)()}`);
        });
    }
    endedListenerHandler() {
        logger_1.logger.info('Reservation ringtone ended');
    }
    initRingtone() {
        if (typeof window == 'undefined') {
            return;
        }
        // Safari & Chrome are very strict about playing audio.
        // They will only play audio if it's user initiated, so we listen for first click here.
        this.once(window.document.body, 'click').then(() => {
            this.createAudio();
            if (this.reservationAudio) {
                this.reservationAudio.addEventListener('ended', this.endedListenerHandler);
            }
            // These event listeners don't need to removed explicitly since the ringtoneAudio is a singleton
            // It's created upon login and destroyed when the user logs out and navigates away from the app.
            logger_1.logger.info(`Adding event listeners to ringtone`);
            AUDIO_EVENTS_TO_CAPTURE.forEach(event => {
                var _a;
                (_a = this.ringtoneAudio) === null || _a === void 0 ? void 0 : _a.addEventListener(event, () => {
                    logger_1.logger.info(`Ringtone audio - received ${event} event`);
                    if (event === 'playing') {
                        this.handleRingtonePlaying();
                    }
                });
            });
            logger_1.logger.info(`Listening on media device changes`);
            navigator.mediaDevices.addEventListener('devicechange', () => __awaiter(this, void 0, void 0, function* () {
                const devices = yield (0, toolbox_1.getMediaDevices)('audiooutput');
                logger_1.logger.info(`Received ondevicechange event. Available media devices are ${JSON.stringify(devices)}`);
                const preferredOutputId = (0, toolbox_1.getPreferredAudioOutputId)();
                const preferredOutputDevice = devices.find((device) => device.deviceId === preferredOutputId);
                logger_1.logger.info(`Preferred output device: ${preferredOutputId} exists: ${JSON.stringify(preferredOutputDevice)}`);
                if (!preferredOutputDevice) {
                    const defaultDevice = devices.find((device) => device.deviceId === 'default');
                    if (defaultDevice) {
                        logger_1.logger.warn(`Could not find preferred preferredOutputId=${preferredOutputId} Resetting preferred
                            device to default: ${JSON.stringify(defaultDevice)}`);
                        (0, toolbox_1.setPreferredAudioOutputId)('default');
                        this.handleAudioOutputDeviceChanged(defaultDevice);
                    }
                    else {
                        logger_1.logger.error('Unexpected state: Default Audio Output device not found!');
                    }
                }
            }));
        });
    }
    // Once the ringtone audio has played, this will prevent the user from accidentally re-playing the audio with their keyboard media keys
    disabledMediaKeys() {
        const mediaActions = [
            'play',
            'pause',
            'stop',
            'seekbackward',
            'seekforward',
            'seekto',
            'previoustrack',
            'nexttrack',
        ];
        mediaActions.forEach(action => {
            try {
                // @ts-ignore
                navigator.mediaSession.setActionHandler(action, () => { });
            }
            catch (e) {
                // Ignoring error, which can happen for the `stop` event on old Android devices
            }
        });
    }
    createAudio() {
        // Safari lacks a 'mediaSession' API, so the 'play/pause' button will still control playback of the loaded audio ringtone
        if ('mediaSession' in navigator) {
            this.disabledMediaKeys();
        }
        if (!this.ringtoneAudio) {
            this.ringtoneAudio = new Audio(`${window.location.origin}/streem_ringtone.mp3`);
            this.ringtoneAudio.loop = true;
        }
        if (!this.reservationAudio) {
            this.reservationAudio = new Audio(`${window.location.origin}/reservation_sound.mp3`);
        }
        logger_1.logger.info('User has interacted with the page, audio is now enabled');
    }
    /**
     * Will run a function once and only once upon a given event on an element
     * @param el The element that will host the event
     * @param event The event to monitor
     */
    once(el, event) {
        return new Promise(resolve => {
            el.addEventListener(event, function onEvent() {
                el.removeEventListener(event, onEvent);
                resolve();
            });
        });
    }
}
exports.audio = new AppAudio();
//# sourceMappingURL=sounds.js.map